import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ClassIcon } from "../components/ClassIcon";
import { useNavigate, useParams } from "react-router-dom";
import api, { ASSETS, wrestlerAvatar } from "../lib/api";
import AvatarLayered from "../components/AvatarLayered";
import { StageUnlockBurst } from "../components/StageUnlockBurst";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpCircle, ArrowDownCircle, Skull, Star, BicepsFlexed, Zap, Moon } from "lucide-react";
import { GradCard } from "../components/wrestler/WrestlerUI";
import { TrainingAmbient, StageRow } from "../components/training/TrainingUI";
import { useInZoom, ScreenPortal } from "../components/MobileZoom";
import { SkillBar, FistStrike, LevelUpBurst } from "../components/training/TrainingFx";
import { TrainingTutorial } from "../components/training/TrainingTutorial";

const TICK_MS = 800;

function xpForLevel(lvl) {
  return 100 + (lvl - 1) * 40 + (lvl - 1) ** 2 * 18;
}

export default function TrainingPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const inZoom = useInZoom();
  const { refresh } = useAuth();
  const [w, setW] = useState(null);
  const [dummy, setDummy] = useState(null);   // {id, name, is_boss, hp, color, kill_number}
  const [dummyHp, setDummyHp] = useState(0);
  const [dummyMax, setDummyMax] = useState(1);
  const [floats, setFloats] = useState([]);   // {id, value, type, x, y}
  const [loot, setLoot] = useState(null);     // gear drop {id, name, image, slot, level, leveled_up, bonus, copies}
  const [trophy, setTrophy] = useState(null); // trophy drop {id, name, color, boss_stage}
  const [card, setCard] = useState(null);     // {dummy_name}
  const [col, setCol] = useState(null);       // {name, qty, stage, image}
  const [stageIntro, setStageIntro] = useState(false);
  const [shake, setShake] = useState(false);
  const [hitKey, setHitKey] = useState(0);
  const [bossIntro, setBossIntro] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [multi, setMulti] = useState(null);     // {key, n} compact multi-KO counter
  const multiTimerRef = useRef(null);
  const [defeated, setDefeated] = useState(null); // {id, name, image, is_boss, color, key} during death animation
  const defeatTimerRef = useRef(null);
  const fidRef = useRef(0);
  const killingRef = useRef(false);
  const shakeRef = useRef(null);
  const plateRef = useRef(null);
  const lvlTimerRef = useRef(null);
  const [lvlPulse, setLvlPulse] = useState(false);
  const [unlockFx, setUnlockFx] = useState(null);
  const unlockTimerRef = useRef(null);
  const wRef = useRef(null);
  wRef.current = w;
  const dummyHpRef = useRef(0);
  dummyHpRef.current = dummyHp;
  const [skillCfg, setSkillCfg] = useState(null);
  const [skillReadyAt, setSkillReadyAt] = useState(0);
  const [skillReveal, setSkillReveal] = useState(false);
  const [fistFx, setFistFx] = useState(null);
  const [lvlBurst, setLvlBurst] = useState(null);
  const [trainTut, setTrainTut] = useState(undefined);
  const [tut, setTut] = useState(null);
  const [gift, setGift] = useState(null);
  const [skillScale, setSkillScale] = useState(1);
  // Skill bar shrinks to fit the gap between the two fighter frames (layout coords, zoom-safe).
  useEffect(() => {
    const f = document.querySelector('[data-testid="training-fighter-frame"]');
    const d = document.querySelector('[data-testid="training-dummy-frame"]');
    if (!f || !d) return;
    const calc = () => {
      const gap = d.offsetLeft - (f.offsetLeft + f.offsetWidth);
      setSkillScale(Math.max(0.35, Math.min(1, (gap - 16) / 330)));
    };
    calc();
    const ro = new ResizeObserver(() => requestAnimationFrame(calc));
    ro.observe(f.parentElement); ro.observe(f); ro.observe(d);
    return () => ro.disconnect();
  }, [w?.wrestler_id, dummy?.is_boss, !!skillCfg, trainTut]); // eslint-disable-line react-hooks/exhaustive-deps
  const fireUnlock = (title) => {
    clearTimeout(unlockTimerRef.current);
    setUnlockFx({ key: Date.now(), title });
    unlockTimerRef.current = setTimeout(() => setUnlockFx(null), 2700);
  };
  useEffect(() => () => { clearTimeout(unlockTimerRef.current); clearTimeout(multiTimerRef.current); clearTimeout(defeatTimerRef.current); }, []);

  useLayoutEffect(() => {
    if (hitKey === 0) return;
    [shakeRef.current, lvlPulse ? null : plateRef.current].forEach(el => {
      if (!el) return;
      el.style.animation = "none";
      void el.offsetWidth;
      el.style.animation = "";
    });
  }, [hitKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const pulseLevelUp = () => {
    clearTimeout(lvlTimerRef.current);
    setLvlPulse(true);
    lvlTimerRef.current = setTimeout(() => setLvlPulse(false), 2400);
  };
  useEffect(() => () => clearTimeout(lvlTimerRef.current), []);

  // First-time boss encounter cinematic (per wrestler + stage, localStorage-tracked)
  useEffect(() => {
    if (!w || !dummy) return;
    const stage = w.training_stage;
    if (![10, 20, 30, 40, 50].includes(stage)) return;
    const key = `rk_boss_seen_${id}_${stage}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    setBossIntro(true);
    const t = setTimeout(() => setBossIntro(false), 1100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [w?.training_stage, dummy?.id, id]);

  const loadAll = async () => {
    const wr = await api.get(`/wrestlers/${id}`);
    setW(wr.data);
    const d = await api.get(`/wrestlers/${id}/training/dummy`);
    setDummy(d.data);
    setDummyMax(d.data.hp);
    setDummyHp(d.data.hp);
    setSkillCfg(d.data.skill);
    setSkillReadyAt(d.data.skill_ready_at ? Date.parse(d.data.skill_ready_at) : 0);
    setTrainTut(d.data.train_tut ?? null);
    if (d.data.train_tut === "mask") {
      setGift((g) => g || { name: "Tiger Mask", image: "/items/face_mask.webp" });
      setTut("mask");
    }
  };

  // Ricky's skill intro: first time a wrestler of this player reaches the skill level.
  useEffect(() => {
    if (tut || trainTut !== null || !w || !skillCfg || w.level < skillCfg.unlock_level) return;
    const t = setTimeout(() => setTut("skill"), 1900);
    return () => clearTimeout(t);
  }, [tut, trainTut, w?.level, skillCfg]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyKillResult = (r, prevW) => {
    const nw = r.data.wrestler;
    const prevMax = Math.max(prevW?.max_stage_reached || 0, prevW?.training_stage || 0);
    const sameStage = prevW && prevW.training_stage === nw.training_stage;
    const crossedReq = sameStage && nw.training_stage >= prevMax && nw.req_kills > 0
      && prevW.training_kills < nw.req_kills && nw.training_kills >= nw.req_kills;
    const hofNow = (nw.max_stage_reached || 0) >= 51 && (prevW?.max_stage_reached || 0) < 51;
    if (crossedReq || hofNow) {
      fireUnlock(nw.training_stage >= 50 ? "Hall of Fame Unlocked!" : `Stage ${nw.training_stage + 1} Unlocked!`);
    }
    setW(nw);
    if ((r.data.kills || 1) > 1) {
      clearTimeout(multiTimerRef.current);
      setMulti({ key: Date.now(), n: r.data.kills });
      multiTimerRef.current = setTimeout(() => setMulti(null), 1400);
    }
    setDummy(r.data.next_dummy);
    setDummyMax(r.data.next_dummy.hp);
    setDummyHp(r.data.next_dummy.hp);
    if (!r.data.kills) return;
    const fidXp = ++fidRef.current;
    setFloats(prev => [...prev, { id: fidXp, value: `+${r.data.xp_gain} XP`, type: "xp", x: 55, y: 50 }]);
    setTimeout(() => setFloats(prev => prev.filter(f => f.id !== fidXp)), 1000);
    const fidHd = ++fidRef.current;
    setFloats(prev => [...prev, { id: fidHd, value: `+${r.data.hot_dogs}`, type: "hotdog", x: 50, y: 55 }]);
    setTimeout(() => setFloats(prev => prev.filter(f => f.id !== fidHd)), 1100);
    if (r.data.leveled) {
      pulseLevelUp();
      setLvlBurst({ key: Date.now(), level: nw.level });
      setTimeout(() => setLvlBurst(null), 1850);
    }
    if (r.data.loot_info) {
      setLoot(r.data.loot_info);
      setTimeout(() => setLoot(null), 5000);
    }
    if (r.data.trophy) {
      setTrophy(r.data.trophy);
      setTimeout(() => setTrophy(null), 6500);
    }
    if (r.data.card) {
      setCard({ id: r.data.card, name: r.data.card_dummy_name, count: r.data.card_count || 1 });
      setTimeout(() => setCard(null), 4500);
    }
    if (r.data.collectible) {
      setCol(r.data.collectible);
      setTimeout(() => setCol(null), 4500);
    }
    refresh();
  };

  const wait = (ms) => new Promise(res => setTimeout(res, ms));
  const castRef = useRef(null);
  const castSkill = async (tries = 0) => {
    if (killingRef.current) {
      // A KO animation is running — retry shortly instead of dropping the click.
      if (tries < 12) setTimeout(() => castRef.current?.(tries + 1), 250);
      return;
    }
    if (!dummy) return;
    killingRef.current = true;
    const prevW = wRef.current;
    const target = dummy;
    setFistFx({ key: Date.now() });
    const impact = wait(540);
    try {
      const r = await api.post(`/wrestlers/${id}/training/skill`, { remaining_hp: dummyHpRef.current });
      setSkillReadyAt(Date.parse(r.data.ready_at));
      await impact;
      [0, 160].forEach((d, i) => setTimeout(() => {
        setHitKey(k => k + 1);
        setShake(true);
        setTimeout(() => setShake(false), 260);
        const fid = ++fidRef.current;
        setFloats(prev => [...prev.slice(-12), { id: fid, value: Math.round(r.data.hit).toLocaleString(), type: "dmg", x: 66 + i * 6, y: 32 + i * 10 }]);
        setTimeout(() => setFloats(prev => prev.filter(f => f.id !== fid)), 1100);
      }, d));
      if (!r.data.kills) {
        await wait(160);
        setDummyHp(Math.max(1, r.data.remaining_hp));
        return;
      }
      await wait(200);
      clearTimeout(defeatTimerRef.current);
      setDefeated({ ...target, key: Date.now() });
      defeatTimerRef.current = setTimeout(() => setDefeated(null), 950);
      await wait(700);
      applyKillResult(r, prevW);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Skill failed");
    } finally {
      setTimeout(() => setFistFx(null), 400);
      killingRef.current = false;
    }
  };
  castRef.current = castSkill;

  useEffect(() => { loadAll(); }, [id]);

  // Mark wrestler as passively training on entry (until they open Promo)
  useEffect(() => {
    api.post(`/wrestlers/${id}/training/enter`).catch(() => {});
  }, [id]);

  // Combat tick
  useEffect(() => {
    if (!w || !dummy) return;
    const interval = setInterval(() => {
      if (killingRef.current) return;
      const dmg = Math.max(1, Math.floor(w.stats.power * (0.8 + Math.random() * 0.4)));
      const x = 55 + Math.random() * 10;
      const y = 40 + Math.random() * 10;
      const fid = ++fidRef.current;
      setHitKey(k => k + 1);
      setFloats(prev => [...prev.slice(-12), { id: fid, value: dmg, type: "dmg", x, y }]);
      setTimeout(() => setFloats(prev => prev.filter(f => f.id !== fid)), 1000);
      setDummyHp(prev => {
        const next = prev - dmg;
        if (next <= 0 && !killingRef.current) {
          killingRef.current = true;
          // Trigger defeat animation on the just-killed dummy
          clearTimeout(defeatTimerRef.current);
          setDefeated({ ...dummy, key: Date.now() });
          defeatTimerRef.current = setTimeout(() => setDefeated(null), 950);
          // Hold the next enemy until the KO animation has played (gains are time-based server-side).
          const holdP = new Promise(res => setTimeout(res, 700));
          (async () => {
            try {
              const prevW = wRef.current;
              const r = await api.post(`/wrestlers/${id}/training/kill`);
              await holdP;
              applyKillResult(r, prevW);
            } catch (e) {
              // 409 = duplicate/raced KO already credited server-side; just resync the enemy.
              if (e.response?.status !== 409) console.warn("training kill failed", e);
              setDummyHp(prev => (prev <= 0 ? dummyMax : prev));
            } finally {
              killingRef.current = false;
            }
          })();
          return 0;
        }
        return Math.max(0, next);
      });
    }, TICK_MS);
    return () => clearInterval(interval);
  }, [w?.wrestler_id, w?.stats?.power, dummy?.id]);

  const advance = async () => {
    setBusy(true);
    try {
      const prevMax = w.max_stage_reached || w.training_stage;
      const nextStage = w.training_stage + 1;
      const isNewTerritory = nextStage > prevMax;
      await api.post(`/wrestlers/${id}/training/advance`);
      await loadAll();
      toast.success(`Stage UP!`);
      if (isNewTerritory) {
        setShake(true);
        setStageIntro(true);
        setTimeout(() => setShake(false), 500);
        setTimeout(() => setStageIntro(false), 900);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const gotoStage = async (n) => {
    if (n === w.training_stage) { setPickerOpen(false); return; }
    setBusy(true);
    try {
      await api.post(`/wrestlers/${id}/training/goto/${n}`);
      await loadAll();
      setPickerOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const goBack = async () => {
    setBusy(true);
    try {
      await api.post(`/wrestlers/${id}/training/back`);
      await loadAll();
      toast.success("Back to easier dummies");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!w || !dummy) return <div className="p-8 text-center font-heading neon-cyan">LOADING...</div>;
  const reqKills = w.req_kills || 0;
  const maxReached = w.max_stage_reached || w.training_stage;
  const isFastForward = w.training_stage < maxReached;
  // Stage 50 is the FINAL stage. Once cleared (boss down, max_stage_reached>=51),
  // there's nothing to advance to — the player has unlocked the Hall of Fame.
  const isFinalCleared = w.training_stage === 50 && maxReached >= 51;
  const canAdvance = !isFinalCleared && (isFastForward || w.training_kills >= reqKills);
  const arenaTier = Math.min(5, Math.floor((w.training_stage - 1) / 10) + 1);
  const canGoBack = w.training_stage > 1;
  const hpPct = Math.max(0, (dummyHp / dummyMax) * 100);
  const xpPct = Math.min(100, (w.xp / xpForLevel(w.level)) * 100);
  const isBoss = dummy.is_boss;

  const killsPct = (isFastForward || isFinalCleared || !reqKills) ? 100 : Math.min(100, (w.training_kills / reqKills) * 100);
  const accent = isBoss ? "#9D00FF" : (dummy.color || "#FF007F");
  const showGhost = defeated && dummyHp <= 0;
  const classColor = w.class_info?.color || "#00F0FF";
  const dummyFrame = `linear-gradient(140deg, ${accent}, #0A051A 55%, ${accent})`;

  return (
    <div
      className="relative overflow-hidden"
      data-testid="training-page"
      style={{
        backgroundImage: `linear-gradient(rgba(10,5,26,0.72), rgba(10,5,26,0.94)), url(${ASSETS.bg.training})`,
        backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: inZoom ? "scroll" : "fixed",
        minHeight: "calc(var(--vh, 1vh) * 100 - 100px)",
      }}
    >
      <TrainingAmbient />
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-3">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3 wr-rise">
          <div className="flex items-center gap-4 min-w-0">
            <button data-testid="training-back-btn" onClick={() => nav(`/wrestler/${id}`)}
                    className="group flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#00F0FF]/40 bg-[#0A051A]/70 text-[#A89BCC] hover:text-[#00F0FF] hover:border-[#00F0FF] hover:shadow-[0_0_12px_#00F0FF66] font-mono text-xs uppercase tracking-widest transition-[color,border-color,box-shadow]">
              <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" /> Back
            </button>
            <h1 className="font-graffiti text-2xl sm:text-4xl leading-none uppercase whitespace-nowrap" data-testid="training-title">
              <span style={{ color: "#FF007F", textShadow: "0 0 18px #FF007F99, 3px 3px 0 #000" }}>Training</span>{" "}
              <span style={{ color: "#00F0FF", textShadow: "0 0 18px #00F0FF99, 3px 3px 0 #000" }}>Arena</span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 bg-[#0A051A]/80" style={{ borderColor: "#FFE60066", boxShadow: "0 0 14px #FFE60033" }}>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#A89BCC]">Stage</span>
              <span data-testid="training-stage" className="font-num text-3xl leading-none tracking-wider text-[#FFE600] tabular-nums min-w-[2ch] text-center" style={{ textShadow: "0 0 12px #FFE60099" }}>{w.training_stage}</span>
              <span className="text-[#A89BCC] text-xs font-mono">/ 50</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 bg-[#0A051A]/80 w-[15rem] sm:w-[17rem] justify-end overflow-hidden" style={{ borderColor: "#FF339966", boxShadow: "0 0 14px #FF339933" }}>
              <Skull size={16} strokeWidth={2.6} className="text-[#FF3399]" style={{ filter: "drop-shadow(0 0 5px #FF3399)" }} />
              <span data-testid="training-kills" className="font-num text-xl leading-none tracking-wider text-[#FF3399] tabular-nums truncate">{w.training_kills.toLocaleString()} / {(isFastForward || isFinalCleared) ? "✓" : reqKills.toLocaleString()} kills</span>
            </div>
          </div>
        </div>

        <StageUnlockBurst fx={unlockFx} />
        {tut && (
          <TrainingTutorial stage={tut} gift={gift} level={w?.level}
            onSkillReveal={() => { setTrainTut("skill"); setSkillReveal(true); api.post("/tutorial/training", { step: "skill" }).catch(() => {}); }}
            onDone={() => {
              if (tut === "mask") { setTrainTut("done"); api.post("/tutorial/training", { step: "done" }).catch(() => {}); }
              setTut(null);
            }} />
        )}

        {/* Arena */}
        <GradCard delay={80} testId="training-arena-card">
          <div className={`relative rounded-xl overflow-hidden border-2 arena-strobe ${isBoss ? "pulse-neon" : ""} ${shake ? "arena-shake" : ""} ${unlockFx ? "arena-slowmo" : ""}`}
               style={{
                 height: "clamp(320px, calc(var(--vh, 1vh) * 100 - 366px), 540px)",
                 borderColor: isBoss ? "#9D00FF" : "#5B3BFF88",
                 boxShadow: isBoss ? "0 0 40px rgba(157,0,255,0.6), inset 0 0 60px rgba(157,0,255,0.25)" : "inset 0 0 50px rgba(10,5,26,0.8)",
                 backgroundImage: `linear-gradient(rgba(10,5,26,0.3), rgba(10,5,26,0.65)), url(/arenas/${arenaTier}.webp)`,
                 backgroundSize: "cover",
                 backgroundPosition: "center",
               }}
               data-testid="training-arena"
               data-arena-tier={arenaTier}>
            {arenaTier >= 2 && (
              <>
                <div className="pointer-events-none absolute -top-8 left-[15%] w-[240px] h-[520px] spot-left"
                     style={{ background: "radial-gradient(ellipse at top, rgba(255,255,255,0.22), rgba(255,0,127,0.15) 40%, transparent 70%)", filter: "blur(6px)" }} />
                <div className="pointer-events-none absolute -top-8 right-[15%] w-[240px] h-[520px] spot-right"
                     style={{ background: "radial-gradient(ellipse at top, rgba(255,255,255,0.22), rgba(0,240,255,0.15) 40%, transparent 70%)", filter: "blur(6px)" }} />
              </>
            )}
            {Array.from({ length: 3 + arenaTier * 2 }).map((_, i) => {
              const left = (i * 137) % 96 + 2;
              const top = (i * 53) % 55 + 3;
              const delay = (i * 0.31) % 1.6;
              const size = 4 + ((i * 7) % 5);
              return (
                <div key={i}
                     className="pointer-events-none absolute rounded-full twinkle"
                     style={{
                       left: `${left}%`, top: `${top}%`,
                       width: size, height: size,
                       background: i % 2 ? "#FFFFFF" : "#FFE600",
                       boxShadow: `0 0 ${8 + size}px ${i % 2 ? "#FFFFFF" : "#FFE600"}`,
                       animationDelay: `${delay}s`,
                     }} />
              );
            })}
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#FF007F]/25 to-transparent pointer-events-none" />
            <div className="wr-grain absolute inset-0 pointer-events-none" />

            {/* Fighter progress HUD (mirrors the enemy HP panel) */}
            <div data-testid="training-progress-hud" className="absolute top-4 left-[4%] w-[min(16rem,44%)] rounded-xl border-2 bg-[#0A051A]/70 backdrop-blur-md px-3 py-2"
                 style={{ borderColor: "#00F0FF88", boxShadow: "0 0 18px #00F0FF44" }}>
              <div className="flex items-center justify-between gap-2 mb-1.5" data-testid="training-xp-block">
                <span className="font-num text-base sm:text-xl leading-none tracking-wider neon-cyan whitespace-nowrap">LV {w.level}</span>
                <span className="flex items-center gap-1 font-num text-lg leading-none tracking-wider" style={{ color: "#FF007F", textShadow: "0 0 8px #FF007F88" }} title="Damage per tick">
                  <BicepsFlexed size={14} strokeWidth={2.6} />
                  <span data-testid="training-dps">{w.stats.power.toLocaleString()}</span>
                  <span className="hidden sm:inline font-mono text-[9px] text-[#A89BCC] tracking-widest">DMG</span>
                </span>
              </div>
              <div className="xp-bar"><div className="xp-fill relative overflow-hidden" style={{ width: `${xpPct}%` }}><span className="wr-xp-shine absolute inset-y-0 w-1/3" /></div></div>
              <div className="text-center font-mono text-[10px] text-[#A89BCC] mt-1">{w.xp.toLocaleString()} / {xpForLevel(w.level).toLocaleString()} XP</div>
              {w.est_wpm && (
                <div data-testid="wpm-display" title="Estimated wins per minute"
                     className="mt-1.5 pt-1.5 border-t border-white/10 flex items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-wider text-[#A89BCC] whitespace-nowrap">
                  <span className="text-[9px] tracking-widest">Wins/min</span>
                  <span className="flex items-center gap-1"><Zap size={11} className="text-[#39FF14]" />
                    <span data-testid="wpm-active" className="text-[#39FF14] font-bold tabular-nums">{w.est_wpm.active.toLocaleString()}</span>
                  </span>
                  <span className="flex items-center gap-1"><Moon size={11} className="text-[#00F0FF]" />
                    <span data-testid="wpm-passive" className="text-[#00F0FF] font-bold tabular-nums">{w.est_wpm.passive.toLocaleString()}</span>
                    <span className="hidden sm:inline opacity-70">{Math.round(w.effective_passive_pct ?? 25)}%</span>
                  </span>
                </div>
              )}
            </div>

            {/* Fighter */}
            <div data-testid="training-fighter-frame" className="absolute bottom-[4%] left-[4%] sm:left-[10%] rounded-xl p-[3px]"
                 style={{ height: "min(calc(17.5rem + 6px), calc(96% - 8.5rem))", aspectRatio: "206 / 286", background: lvlPulse ? "linear-gradient(140deg,#FFE600,#FF9500,#FFE600)" : `linear-gradient(140deg, ${classColor}, #0A051A 55%, ${classColor})`, boxShadow: `0 0 30px ${lvlPulse ? "#FFE600" : classColor}88` }}>
              <div data-testid="training-fighter-box" className="w-full h-full rounded-[10px] overflow-hidden flex flex-col bg-[#110726]">
                <AvatarLayered fgSrc={wrestlerAvatar(w)} promo={w.owner_promotion} alt="fighter" className="w-full flex-1 min-h-0 border-b-2 transition-colors duration-300" style={{ borderColor: lvlPulse ? "#FFE600" : classColor }} testId="training-fighter-avatar" />
                <div ref={plateRef} data-testid="training-fighter-plate" data-state={lvlPulse ? "levelup" : "idle"}
                     style={{ background: `linear-gradient(180deg, ${classColor}26, #0A051A)` }}
                     className={`shrink-0 h-[52px] flex flex-col items-center justify-center gap-1 px-2 ${lvlPulse ? "plate-levelup" : "plate-hit"}`}>
                  <div data-testid="training-fighter-name" title={w.name} className={`w-full truncate text-center font-graffiti text-base uppercase leading-none ${lvlPulse ? "neon-yellow" : "text-white"}`} style={lvlPulse ? undefined : { textShadow: `0 0 10px ${classColor}, 2px 2px 0 #000` }}>{lvlPulse ? "LEVEL UP!" : w.name}</div>
                  <div data-testid="training-fighter-meta" className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider leading-none whitespace-nowrap">
                    <span className="px-1.5 py-0.5 rounded bg-[#00F0FF] text-[#0A051A] font-bold whitespace-nowrap">LV {w.level}</span>
                    {w.class_info?.name && (
                      <span className="truncate inline-flex items-center gap-1" style={{ color: w.class_info.color || "#FF007F" }}><ClassIcon classId={w.class_info.id} color={w.class_info.color || "#FF007F"} size={11} testId="training-class-icon" />{w.class_info.name}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* VS mark */}
            <div className="hidden sm:block pointer-events-none absolute left-1/2 bottom-[28%] -translate-x-1/2 font-graffiti text-6xl opacity-80 wr-crown"
                 style={{ color: "#FFE600", textShadow: "0 0 20px #FF007F, 4px 4px 0 #000" }} aria-hidden="true">VS</div>

            {/* Dummy */}
            <div data-testid="training-dummy-frame" className={`absolute bottom-[4%] right-[4%] sm:right-[10%] rounded-xl p-[3px] ${stageIntro ? "stage-intro" : ""}`}
                 style={{ height: isBoss ? "min(calc(19rem + 6px), calc(96% - 8.5rem))" : "min(calc(17.5rem + 6px), calc(96% - 8.5rem))", aspectRatio: isBoss ? "222 / 310" : "186 / 286", background: dummyFrame, boxShadow: `0 0 ${isBoss ? 36 : 22}px ${accent}99` }}>
              {multi && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-30 pointer-events-none">
                  <div key={multi.key} data-testid="multi-ko-counter"
                       className="kill-pop flex items-center gap-1 px-2.5 py-1 rounded-lg border-2 font-num text-xl leading-none tracking-wider whitespace-nowrap"
                       style={{ color: "#FFE600", borderColor: "#FFE600", background: "rgba(10,5,26,0.88)", textShadow: "0 0 10px #FFE600", boxShadow: "0 0 16px #FFE60077" }}>
                    <Skull size={14} /> ×{multi.n.toLocaleString()} KO
                  </div>
                </div>
              )}
              <div className={`relative rounded-[10px] overflow-hidden bg-[#1C0D40] w-full h-full`}>
                <div ref={shakeRef} className="w-full h-full hit-shake" data-testid="dummy-hit-shake">
                  {dummy.image ? (
                    <img
                      key={`live-${dummy.kill_number ?? dummy.id}`}
                      src={dummy.image}
                      alt={dummy.name}
                      className={`w-full h-full object-cover ${showGhost ? "opacity-0" : "tr-spawn"}`}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {isBoss ? (
                        <Skull size={80} style={{ color: "#9D00FF", filter: 'drop-shadow(0 0 14px #9D00FF)' }} />
                      ) : (
                        <div className="text-6xl font-black"
                             style={{ color: dummy.color, filter: `drop-shadow(0 0 12px ${dummy.color})` }}>X</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 text-center py-1 bg-black/70 backdrop-blur-sm font-heading text-[10px] uppercase tracking-[0.3em]"
                     style={{ color: isBoss ? "#FF3399" : "#E5DCFF" }}>
                  {isBoss ? "BOSS" : "DUMMY"}
                </div>
              </div>
              {/* KO ghost + stamp: outside the clipped box so the full fall + stamp stay visible */}
              {defeated && (
                <div key={defeated.key} className="absolute inset-0 pointer-events-none">
                  <div className="tr-ko-flash absolute inset-0 rounded-xl z-[5]" style={{ background: `radial-gradient(circle, #FFFFFFcc, ${defeated.color || "#FF007F"}66 45%, transparent 70%)` }} />
                  {defeated.image && (
                    <div data-testid="defeat-ghost-mask" className="absolute inset-[3px] overflow-hidden rounded-[10px] z-10">
                      <img data-testid="defeat-ghost" src={defeated.image} alt="" className="npc-defeat w-full h-full object-cover" />
                    </div>
                  )}
                  <div data-testid="defeat-frame-ring" className="absolute inset-0 rounded-xl z-[15]"
                       style={{ border: "3px solid transparent", background: `${dummyFrame} border-box`,
                                WebkitMask: "linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0)", WebkitMaskComposite: "xor", mask: "linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0)", maskComposite: "exclude" }} />
                  <div className="absolute inset-0 z-20"><div data-testid="defeat-stamp" className="defeat-stamp">DEFEATED</div></div>
                </div>
              )}
            </div>

            <FistStrike fx={fistFx} />
            <LevelUpBurst fx={lvlBurst} />
            {skillCfg && w.level >= skillCfg.unlock_level && trainTut && (
              <div className="absolute bottom-[4%] left-1/2 z-30" style={{ transform: `translateX(-50%) scale(${skillScale})`, transformOrigin: "bottom center" }}>
                <SkillBar readyAt={skillReadyAt} cooldownS={skillCfg.cooldown_s} onUse={() => castSkill()} reveal={skillReveal} disabled={!!fistFx} />
              </div>
            )}

            {isBoss && (
              <div data-testid="boss-banner" className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-2 rounded-xl border-2 border-[#9D00FF] bg-[#9D00FF]/25 backdrop-blur-md shadow-[0_0_24px_#9D00FF88]">
                <span className="font-graffiti text-2xl uppercase" style={{ color: "#FF3399", textShadow: "0 0 12px #9D00FF, 2px 2px 0 #000" }}>Boss Fight</span>
              </div>
            )}

            {/* Dummy name + HP */}
            <div className="absolute top-4 right-[4%] w-[min(16rem,44%)] rounded-xl border-2 bg-[#0A051A]/70 backdrop-blur-md px-3 py-2"
                 style={{ borderColor: `${accent}88`, boxShadow: `0 0 18px ${accent}44` }}>
              <div data-testid="dummy-name"
                   className="text-center font-graffiti text-lg mb-1.5 truncate uppercase leading-none"
                   style={{ color: accent, textShadow: `0 0 10px ${accent}99, 2px 2px 0 #000` }}>
                {dummy.name}
              </div>
              <div className="hp-bar">
                <div className="hp-fill" style={{ width: `${hpPct}%` }} />
              </div>
              <div className="text-center font-num text-base leading-none tracking-wider neon-pink mt-1">{Math.max(0, Math.round(dummyHp))} / {dummyMax}</div>
            </div>

            {floats.map(f => (
              <div
                key={f.id}
                className="dmg-number flex items-center gap-1"
                style={{
                  left: `${f.x}%`,
                  top: `${f.y}%`,
                  color: f.type === "xp" ? "#FFE600" : (f.type === "hotdog" ? "#FFE600" : "#FF3399"),
                  fontSize: f.type === "xp" ? "2rem" : (f.type === "hotdog" ? "1.4rem" : "1.5rem"),
                }}
              >
                {f.type === "hotdog" && <img src={ASSETS.currency.hotdog} alt="" className="w-6 h-6 inline-block" />}
                {f.type === "dmg" && `-${f.value}`}
                {f.type === "xp" && f.value}
                {f.type === "hotdog" && f.value}
              </div>
            ))}

            {bossIntro && (
              <div className="absolute inset-0 z-40 pointer-events-none overflow-hidden" data-testid="boss-intro">
                <div className="absolute left-0 top-0 bottom-0 w-1/2 boss-curtain-left"
                     style={{ background: "linear-gradient(90deg, #FF007F 0%, #9D00FF 100%)", boxShadow: "inset -30px 0 60px rgba(0,0,0,0.4)" }} />
                <div className="absolute right-0 top-0 bottom-0 w-1/2 boss-curtain-right"
                     style={{ background: "linear-gradient(-90deg, #FF007F 0%, #9D00FF 100%)", boxShadow: "inset 30px 0 60px rgba(0,0,0,0.4)" }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="boss-name-card text-center px-8 py-4 border-4 rounded"
                       style={{ borderColor: "#FFE600", background: "rgba(10,5,26,0.8)", boxShadow: "0 0 50px #9D00FF, inset 0 0 20px rgba(255,230,0,0.3)" }}>
                    <div className="font-heading text-sm neon-cyan tracking-[0.5em]">★ BOSS ★</div>
                    <div className="font-heading text-5xl mt-1"
                         style={{ color: "#FFE600", textShadow: "0 0 22px #FFE600, 0 0 6px #FF007F" }}>{dummy.name}</div>
                    <div className="font-mono text-xs mt-2 tracking-[0.3em]" style={{ color: "#FF007F" }}>STAGE {w.training_stage}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </GradCard>

        {/* Stages block */}
        <GradCard delay={180} testId="training-stages-card" className="mt-3">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-[#A89BCC] mb-1">
              <span>Stage {w.training_stage} clear</span>
              <span className="text-[#FFE600]">{(isFastForward || isFinalCleared) ? "Cleared" : `${Math.floor(killsPct)}%`}</span>
            </div>
            <div className="h-2.5 rounded-full bg-[#0A051A] border border-[#FFE600]/30 overflow-hidden mb-2.5" data-testid="stage-kill-progress">
              <div className="relative h-full rounded-full overflow-hidden transition-[width] duration-500" style={{ width: `${killsPct}%`, background: "linear-gradient(90deg,#FF9500,#FFE600)", boxShadow: "0 0 10px #FFE600" }}>
                <span className="tr-bar-shine absolute inset-y-0 w-1/3" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StageRow label="Previous Stage" color="#00F0FF" align="left"
                picker={<button data-testid="prev-stage-picker-btn" onClick={() => setPickerOpen(true)} disabled={busy}
                                className="font-num text-xl leading-none tracking-wider neon-cyan hover:brightness-125 hover:underline decoration-dotted underline-offset-4 transition"
                                title="Jump to any cleared stage">Stage {Math.max(1, w.training_stage - 1)}</button>}
                action={<button data-testid="back-stage-btn" disabled={!canGoBack || busy} onClick={goBack} className="btn-cyan flex items-center gap-2 text-xs">
                          <ArrowDownCircle size={16} /> Back
                        </button>} />
              <StageRow label={isFinalCleared ? "Final Stage" : (isFastForward ? "Next (Cleared)" : "Next Stage")} color={isFinalCleared ? "#39FF14" : "#FFE600"} align="right"
                picker={isFinalCleared ? (
                  <div data-testid="training-final-label" className="font-num text-xl leading-none tracking-wider uppercase" style={{ color: "#39FF14", textShadow: "0 0 10px #39FF1488" }}>— Cleared —</div>
                ) : (
                  <button data-testid="next-stage-picker-btn" onClick={() => setPickerOpen(true)} disabled={busy}
                          className="font-num text-xl leading-none tracking-wider neon-yellow hover:brightness-125 hover:underline decoration-dotted underline-offset-4 transition"
                          title="Jump to any cleared stage">Stage {w.training_stage + 1}</button>
                )}
                action={isFinalCleared ? (
                  <button data-testid="training-hof-unlocked-btn" onClick={() => nav("/hof")}
                          className="flex items-center gap-2 px-3 py-2 rounded-md font-heading uppercase tracking-widest text-[10px] transition-transform hover:scale-[1.04]"
                          style={{ background: "linear-gradient(135deg, #0A051A, #0A2A14)", color: "#39FF14", border: "2px solid #39FF14", boxShadow: "0 0 14px #39FF1488, inset 0 0 10px #39FF1422", textShadow: "0 0 8px #39FF14" }}
                          title="Open the Hall of Fame">
                    <Star size={14} fill="#39FF14" /> Hall of Fame
                  </button>
                ) : (
                  <button data-testid="advance-stage-btn" disabled={!canAdvance || busy} onClick={advance}
                          className="btn-neon flex items-center gap-2 text-xs"
                          title={isFastForward ? "Already cleared — instant advance" : canAdvance ? "Advance to the next stage" : `Defeat ${Math.max(0, reqKills - w.training_kills).toLocaleString()} more enemies to advance`}>
                    {!canAdvance ? (
                      <span key={w.training_kills} data-testid="advance-kills-left" className="flex items-center gap-1 font-mono whitespace-nowrap kill-pop">
                        <Skull size={14} />{Math.max(0, reqKills - w.training_kills).toLocaleString()}
                      </span>
                    ) : (
                      <><ArrowUpCircle size={16} /> {isFastForward ? "Skip" : "Advance"}</>
                    )}
                  </button>
                )} />
            </div>
          </div>
        </GradCard>

        {/* Stage picker modal */}
        {pickerOpen && (<ScreenPortal>
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0A051A]/80 backdrop-blur-sm"
               onClick={() => setPickerOpen(false)}
               data-testid="stage-picker">
            <div className="card-surface p-6 max-w-2xl w-full border-2"
                 style={{ borderColor: "#00F0FF", boxShadow: "0 0 40px rgba(0,240,255,0.4)" }}
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-heading text-2xl neon-cyan">SELECT STAGE</div>
                  <div className="text-xs font-mono text-[#A89BCC] mt-1">
                    Cleared up to <span className="neon-yellow">Stage {maxReached}</span> · locked stages need to be earned
                  </div>
                </div>
                <button
                  onClick={() => setPickerOpen(false)}
                  className="text-[#A89BCC] hover:text-white text-2xl font-heading leading-none"
                  data-testid="stage-picker-close"
                >×</button>
              </div>
              <div className="grid grid-cols-10 gap-2">
                {Array.from({ length: 50 }, (_, i) => i + 1).map((n) => {
                  const locked = n > maxReached;
                  const isCurrent = n === w.training_stage;
                  const isBossStage = [10, 20, 30, 40, 50].includes(n);
                  return (
                    <button
                      key={n}
                      disabled={locked || busy}
                      onClick={() => gotoStage(n)}
                      data-testid={`stage-pick-${n}`}
                      className={`aspect-square font-heading text-sm rounded transition
                                  ${locked ? "opacity-30 cursor-not-allowed" : "hover:scale-110 hover:brightness-125"}
                                  ${isCurrent ? "ring-2" : ""}`}
                      style={{
                        background: locked
                          ? "#1C0D40"
                          : isBossStage
                            ? "linear-gradient(135deg, #9D00FF, #FF007F)"
                            : "linear-gradient(135deg, #FF007F33, #00F0FF33)",
                        color: locked ? "#695D8E" : "#FFFFFF",
                        borderColor: isBossStage ? "#FFE600" : "#00F0FF",
                        borderWidth: isBossStage ? 2 : 1,
                        borderStyle: "solid",
                        boxShadow: isCurrent ? "0 0 14px #00F0FF" : "none",
                        textShadow: isBossStage && !locked ? "0 0 6px #FFE600" : "none",
                      }}
                      title={locked ? "Locked" : (isBossStage ? "BOSS" : `Stage ${n}`)}
                    >{n}</button>
                  );
                })}
              </div>
              <div className="mt-4 flex items-center gap-4 text-[10px] font-mono text-[#A89BCC]">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: "linear-gradient(135deg,#9D00FF,#FF007F)" }} /> Boss</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: "linear-gradient(135deg,#FF007F33,#00F0FF33)", border: "1px solid #00F0FF" }} /> Cleared</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-[#1C0D40] opacity-30" /> Locked</span>
              </div>
            </div>
          </div>
        </ScreenPortal>)}

        {/* Gear drop popup */}
        {loot && (
          <div className="fixed top-[20rem] left-1/2 -translate-x-1/2 z-50 pointer-events-none">
            <div className="loot-pop card-surface p-4 pointer-events-auto cursor-pointer border-2 flex items-center gap-3"
                 style={{ borderColor: "#FFE600", boxShadow: "0 0 30px rgba(255,230,0,0.55)", minWidth: 300 }}
                 onClick={() => setLoot(null)}
                 data-testid="gear-drop">
              <div className="w-14 h-14 rounded overflow-hidden bg-[#0A051A] shrink-0 flex items-center justify-center">
                {loot.image ? (
                  <img src={loot.image} alt={loot.name}
                       className="w-full h-full object-contain p-1"
                       onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : <Star size={26} className="text-[#FFE600]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-lg" style={{ color: "#FFE600", textShadow: "0 0 10px #FFE600" }}>
                  {loot.leveled_up ? `⚡ ${loot.slot === "food" ? "FOOD" : "GEAR"} LEVEL UP!` : `+ ${loot.slot === "food" ? "FOOD" : "GEAR"}!`}
                </div>
                <div className="font-mono text-sm text-white truncate">
                  {loot.name} <span className="text-[10px] text-[#A89BCC]">LV {loot.level}</span>
                </div>
                <div className="font-mono text-[10px] text-[#A89BCC] mt-0.5 capitalize">{loot.slot} · x{loot.copies}</div>
              </div>
            </div>
          </div>
        )}

        {/* Card drop popup */}
        {card && (
          <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
            <div className="loot-pop card-surface p-5 pointer-events-auto cursor-pointer border-2"
                 style={{ borderColor: "#9D00FF", boxShadow: "0 0 30px rgba(157,0,255,0.7)" }}
                 onClick={() => setCard(null)}
                 data-testid="card-drop">
              <div className="font-heading text-2xl text-center mb-1" style={{ color: "#9D00FF", textShadow: "0 0 12px #9D00FF" }}>★ CARD DROP ★</div>
              <div className="font-mono text-sm text-center text-white">{card.name}{card.count > 1 && <span data-testid="card-drop-count" className="neon-yellow ml-1">×{card.count}</span>}</div>
              <div className="font-mono text-[10px] text-center text-[#A89BCC] mt-1">Added to collection</div>
            </div>
          </div>
        )}

        {/* Collectible drop popup */}
        {col && (
          <div className="fixed top-[13.5rem] left-1/2 -translate-x-1/2 z-50 pointer-events-none">
            <div className="loot-pop card-surface p-4 pointer-events-auto cursor-pointer border-2 flex items-center gap-3"
                 style={{ borderColor: "#39FF14", boxShadow: "0 0 26px rgba(57,255,20,0.6)", minWidth: 260 }}
                 onClick={() => setCol(null)}
                 data-testid="collectible-drop">
              <div className="w-14 h-14 rounded overflow-hidden bg-[#0A051A] shrink-0">
                <img src={`/collectibles/${col.id}.webp`} alt={col.name}
                     className="w-full h-full object-cover"
                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
              <div className="flex-1">
                <div className="font-heading text-lg" style={{ color: "#39FF14", textShadow: "0 0 10px #39FF14" }}>+ COLLECTIBLE!</div>
                <div className="font-mono text-sm text-white truncate">{col.name}</div>
                <div className="font-mono text-[10px] text-[#A89BCC] mt-0.5">x{(col.qty || 1).toLocaleString()} · Stage {col.stage}</div>
              </div>
            </div>
          </div>
        )}
        {/* Trophy drop popup */}
        {trophy && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
            <div className="loot-pop card-surface p-4 pointer-events-auto cursor-pointer border-2 flex items-center gap-3"
                 style={{ borderColor: trophy.color || "#9D00FF", boxShadow: `0 0 40px ${(trophy.color || "#9D00FF")}bb`, minWidth: 340 }}
                 onClick={() => setTrophy(null)}
                 data-testid="trophy-drop">
              <div className="w-16 h-16 rounded overflow-hidden bg-[#0A051A] shrink-0">
                <img src={`/trophies/${trophy.id}.webp`} alt={trophy.name}
                     className="w-full h-full object-cover"
                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-xl"
                     style={{ color: trophy.color || "#9D00FF", textShadow: `0 0 14px ${trophy.color || "#9D00FF"}` }}>
                  ★ TROPHY UNLOCKED ★
                </div>
                <div className="font-mono text-sm text-white truncate">{trophy.name}</div>
                <div className="font-mono text-[10px] text-[#A89BCC] mt-0.5">Boss Stage {trophy.boss_stage}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
